// src/RootPage.jsx
import React, { useContext, useState, useMemo } from 'react';
import { UserProfileContext } from '../contexts/UserProfileContext';
import SearchBar from '../components/SearchBar';
import AssetFilterButtons from '../components/AssetFilterButtons';
import AccessCard from '../components/AccessCard';
import { AccessContext } from '../contexts/AccessProvider';
import ErrorView from '../components/errors/ErrorView';
import LoadingView from '../../shared/components/LoadingView';
import NoAccessesView from '../components/errors/NoAccessesView';

function assetHasOpenTasks(asset) {
  if (asset?.tasks && Object.keys(asset.tasks).length > 0) return true;
  return (asset?._open_task_count ?? 0) > 0;
}

function historyRecencyScore(entry) {
  if (!entry || typeof entry !== 'object') return 0;
  if (entry.access_duration_until) {
    return new Date(entry.access_duration_until).getTime();
  }
  if (entry.updated_at) {
    return new Date(entry.updated_at).getTime();
  }
  return 0;
}

/** One row per asset id; when missing id, key by access_key string. Keeps the most recent by expiry / updated_at. */
function dedupeAccessHistory(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const map = new Map();
  for (const e of entries) {
    const dedupeKey =
      e?.id != null && e.id !== ''
        ? `id:${e.id}`
        : `key:${e?.access_key ?? ''}`;
    const cur = map.get(dedupeKey);
    if (!cur || historyRecencyScore(e) >= historyRecencyScore(cur)) {
      map.set(dedupeKey, e);
    }
  }
  return Array.from(map.values());
}

export default function RootPage() {
  const { userProfile } = useContext(UserProfileContext);
  const { assets, loading, error } = useContext(AccessContext);

  // Filter and search state
  const [assetFilter, setAssetFilter] = useState('active'); // 'active', 'valid', 'history'
  const [search, setSearch] = useState('');

  // Filtering logic
  const filteredAssets = useMemo(() => {
    let list = [];
    if (assetFilter === 'active') {
      list = assets.filter((a) => assetHasOpenTasks(a));
    } else if (assetFilter === 'valid') {
      list = assets.filter((a) => !assetHasOpenTasks(a));
    } else if (assetFilter === 'history') {
      list = dedupeAccessHistory(userProfile.access_history || []);
    }
    // Search logic
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(asset => {
      // Asset fields
      const assetFields = [
        asset.asset_name,
        asset.asset_make,
        asset.asset_model,
        asset.street_address,
        asset.city,
        asset.status,
      ].map(v => (v || '').toString().toLowerCase());
      // Task fields
      const taskFields = Object.values(asset.tasks || {}).flatMap(task => [
        task.instructions,
        task.ticket_reference,
        task.workflow?.name,
      ].map(v => (v || '').toString().toLowerCase()));
      // Combine all fields
      return [...assetFields, ...taskFields].some(field => field.includes(q));
    });
  }, [assets, userProfile.access_history, assetFilter, search]);

  if (loading) {
    return (
      <LoadingView
        title="Loading Your Assets"
        subtitle="Hang tight while we get everything sorted"
        extra="This should only take a few seconds..."
        fullViewport
      />
    );
  }
  if (error) {
    return (
      <ErrorView
        title="Connection Lost"
        message="We couldn't load the latest data from the backend."
        onRetry={() => window.location.reload()}
        onGoBack={() => window.history.back()}
      />
    );
  }

  return (
    <>
      <div className="bg-blue-100" style={{ minHeight: '100vh' }}>
        <div className="container mt-3">
          {/* Search Bar */}
          <SearchBar value={search} onChange={setSearch} />
          {/* Filter Buttons */}
          <AssetFilterButtons selected={assetFilter} onSelect={setAssetFilter} />
          {/* Accesses List */}
          {filteredAssets.length === 0 ? (
            <NoAccessesView />
          ) : (
            filteredAssets.map((asset, idx) => (
              <AccessCard
                key={`${assetFilter}-${asset.id ?? asset.access_key}-${idx}`}
                asset={asset}
                disableLink={assetFilter === 'history'}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}